import { lazy, Suspense } from 'react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { LoadingScreen } from '@/components/ui'

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const TwoFactorVerify = lazy(() => import('@/pages/auth/TwoFactorVerify'))
const GoogleCallback = lazy(() => import('@/pages/auth/GoogleCallback'))

const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'))

const ProjectsListPage = lazy(() => import('@/pages/projects/ProjectsListPage'))
const ProjectDetailPage = lazy(() => import('@/pages/projects/ProjectDetailPage'))
const ProjectCreatePage = lazy(() => import('@/pages/projects/ProjectCreatePage'))

const PortfoliosListPage = lazy(() => import('@/pages/portfolios/PortfoliosListPage'))
const PortfolioDetailPage = lazy(() => import('@/pages/portfolios/PortfolioDetailPage'))

const ResourcesPage = lazy(() => import('@/pages/resources/ResourcesPage'))
const TimesheetPage = lazy(() => import('@/pages/time/TimesheetPage'))
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'))
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'))
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'))

// Layout components
const MainLayout = lazy(() => import('@/components/layout/MainLayout'))
const AuthLayout = lazy(() => import('@/components/layout/AuthLayout'))

// Guards
const ProtectedRoute = lazy(() => import('@/components/ui/ProtectedRoute'))

const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))

// Suspense wrapper component for lazy loaded routes
const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingScreen />}>
    {children}
  </Suspense>
)

// Protected layout wrapper - wraps MainLayout with ProtectedRoute guard
const ProtectedLayoutWrapper = () => (
  <SuspenseWrapper>
    <ProtectedRoute>
      <MainLayout />
    </ProtectedRoute>
  </SuspenseWrapper>
)

// Auth layout wrapper - for public auth pages
const AuthLayoutWrapper = () => (
  <SuspenseWrapper>
    <AuthLayout />
  </SuspenseWrapper>
)

// Create the router with all routes
export const router = createBrowserRouter([
  // Public auth routes
  {
    path: '/auth',
    element: <AuthLayoutWrapper />,
    children: [
      {
        path: 'login',
        element: (
          <SuspenseWrapper>
            <LoginPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'register',
        element: (
          <SuspenseWrapper>
            <RegisterPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <SuspenseWrapper>
            <ForgotPasswordPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: '2fa',
        element: (
          <SuspenseWrapper>
            <TwoFactorVerify />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'google/callback',
        element: (
          <SuspenseWrapper>
            <GoogleCallback />
          </SuspenseWrapper>
        ),
      },
    ],
  },
  // Protected routes - requires authentication
  {
    path: '/',
    element: <ProtectedLayoutWrapper />,
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <DashboardPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'dashboard',
        element: (
          <SuspenseWrapper>
            <DashboardPage />
          </SuspenseWrapper>
        ),
      },
      // Projects
      {
        path: 'projects',
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <ProjectsListPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'new',
            element: (
              <SuspenseWrapper>
                <ProjectCreatePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: ':projectId',
            element: (
              <SuspenseWrapper>
                <ProjectDetailPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      // Portfolios
      {
        path: 'portfolios',
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <PortfoliosListPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: ':portfolioId',
            element: (
              <SuspenseWrapper>
                <PortfolioDetailPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      // Resources
      {
        path: 'resources',
        element: (
          <SuspenseWrapper>
            <ResourcesPage />
          </SuspenseWrapper>
        ),
      },
      // Time Tracking
      {
        path: 'time',
        element: (
          <SuspenseWrapper>
            <TimesheetPage />
          </SuspenseWrapper>
        ),
      },
      // Reports
      {
        path: 'reports',
        element: (
          <SuspenseWrapper>
            <ReportsPage />
          </SuspenseWrapper>
        ),
      },
      // Settings
      {
        path: 'settings',
        element: (
          <SuspenseWrapper>
            <SettingsPage />
          </SuspenseWrapper>
        ),
      },
      // Admin (requires admin role)
      {
        path: 'admin',
        element: (
          <SuspenseWrapper>
            <ProtectedRoute requiredRoles={['system_admin', 'head_of_pmo']}>
              <AdminPage />
            </ProtectedRoute>
          </SuspenseWrapper>
        ),
      },
    ],
  },
  // 404 Not Found - catch-all for unmatched routes
  {
    path: '*',
    element: (
      <SuspenseWrapper>
        <NotFoundPage />
      </SuspenseWrapper>
    ),
  },
])

// Router component for use in App
export function AppRouter() {
  return <RouterProvider router={router} />
}
